import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import dotenv from "dotenv"
import * as urls from "../../helpers/url_helper"

dotenv.config()
const url = process.env.LOCALHOST
  ? process.env.LOCALHOST
  : "https://qa_api_gtc.amerck.com"

export const templateAPI = createApi({
  reducerPath: "templateApi",
  baseQuery: fetchBaseQuery({ baseUrl: url, timeout: 10000 }),
  tagTypes: ['Template', 'MessageTemplate', 'DropdownData', 'TemplateLanguage', 'TemplateMessage'],
  endpoints: (builder) => ({
    getTemplates: builder.query({
      query: () => ({
        url: urls._GET_TEMPLATES_LIST,
        method: "GET",
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      providesTags: ['Template'],
      transformResponse: (response) => {
        // Handle different response formats
        if (response?.templates && Array.isArray(response.templates)) {
          return response;
        } else if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),
    getTemplateById: builder.query({
      query: (id) => ({
        url: urls._SELECTED_TEMPLATE,
        method: 'POST',
        body: { id },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      providesTags: (result, error, id) => [{ type: 'Template', id }]
    }),
    createTemplate: builder.mutation({
      query: (template) => ({
        url: urls._UPLOAD_TEMPLATE,
        method: 'POST',
        body: template,
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      invalidatesTags: ['Template']
    }),
    updateTemplate: builder.mutation({
      query: ({ id, template_name, sender }) => ({
        url: urls._EDIT_TEMPLATE,
        method: 'POST',
        body: { id, template_name, sender },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      invalidatesTags: ['Template']
    }),
    deleteTemplate: builder.mutation({
      query: (id) => ({
        url: urls._DELETE_TEMPLATE,
        method: 'POST',
        body: { id },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      invalidatesTags: ['Template']
    }),
    // Template languages endpoints for RunTest
    getTemplateLanguages: builder.query({
      query: (templateId) => ({
        url: `${urls._GET_TEMPLATE_LANGUAGES}/${templateId}/languages`,
        method: "GET",
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      providesTags: ['TemplateLanguage']
    }),
    // Template messages endpoints for RunTest
    getTemplateMessages: builder.query({
      query: ({ templateId, languageId }) => ({
        url: `${urls._GET_TEMPLATE_MESSAGES}/${templateId}/language/${languageId}`,
        method: "GET",
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      providesTags: ['TemplateMessage']
    }),
    // Message templates endpoints
    getMessageTemplates: builder.query({
      query: (templateId) => ({
        url: `${urls._GET_TEST_TEMPLATES}/${templateId}`,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      providesTags: (result, error, id) => 
        result 
          ? [
              ...result.map(({ id }) => ({ type: 'MessageTemplate', id })),
              { type: 'MessageTemplate', id: 'LIST' }
            ]
          : [{ type: 'MessageTemplate', id: 'LIST' }]
    }),
    createMessageTemplate: builder.mutation({
      query: (messageTemplate) => ({
        url: urls._GET_TEST_TEMPLATES,
        method: 'POST',
        body: messageTemplate,
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response) => response.messageTemplate,
      invalidatesTags: ['MessageTemplate', 'Template']
    }),
    updateMessageTemplate: builder.mutation({
      query: ({ id, ...messageTemplate }) => ({
        url: `${urls._GET_TEST_TEMPLATES}/${id}`,
        method: 'PUT',
        body: messageTemplate,
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response) => response.messageTemplate,
      invalidatesTags: (result, error, { id }) => [{ type: 'MessageTemplate', id }]
    }),
    deleteMessageTemplate: builder.mutation({
      query: (id) => ({
        url: `${urls._GET_TEST_TEMPLATES}/${id}`,
        method: 'DELETE',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      invalidatesTags: ['MessageTemplate', 'Template']
    }),
    // Dropdown data endpoints
    getLanguages: builder.query({
      query: () => ({
        url: urls._GET_DROPDOWN_LANGUAGES,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response) => response.languages || [],
      providesTags: ['DropdownData']
    }),
    getFormats: builder.query({
      query: () => ({
        url: urls._GET_DROPDOWN_FORMATS,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response) => response.formats || [],
      providesTags: ['DropdownData']
    }),
    getTypes: builder.query({
      query: () => ({
        url: urls._GET_DROPDOWN_TYPES,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response) => response.types || [],
      providesTags: ['DropdownData']
    }),
    createDropdownItem: builder.mutation({
      query: ({ category, value }) => ({
        url: `${urls._CREATE_DROPDOWN_ITEM}/${category}`,
        method: 'POST',
        body: { value },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response, meta, arg) => {
        // Extract the correct property based on category
        const categoryMapping = {
          'languages': 'language',
          'formats': 'format',
          'types': 'type'
        };
        const key = categoryMapping[arg.category];
        return response[key];
      },
      invalidatesTags: ['DropdownData']
    }),
    updateDropdownItem: builder.mutation({
      query: ({ category, id, value }) => ({
        url: `${urls._UPDATE_DROPDOWN_ITEM}/${category}/${id}`,
        method: 'PUT',
        body: { value },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      transformResponse: (response, meta, arg) => {
        // Extract the correct property based on category
        const categoryMapping = {
          'languages': 'language',
          'formats': 'format',
          'types': 'type'
        };
        const key = categoryMapping[arg.category];
        return response[key];
      },
      invalidatesTags: ['DropdownData']
    }),
    deleteDropdownItem: builder.mutation({
      query: ({ category, id }) => ({
        url: `${urls._DELETE_DROPDOWN_ITEM}/${category}/${id}`,
        method: 'DELETE',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 4000,
      }),
      invalidatesTags: ['DropdownData']
    })
  }),
})

// Export generated hooks
export const {
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateLanguagesQuery,
  useLazyGetTemplateLanguagesQuery,
  useGetTemplateMessagesQuery,
  useLazyGetTemplateMessagesQuery,
  useGetMessageTemplatesQuery,
  useCreateMessageTemplateMutation,
  useUpdateMessageTemplateMutation,
  useDeleteMessageTemplateMutation,
  useGetLanguagesQuery,
  useGetFormatsQuery,
  useGetTypesQuery,
  useCreateDropdownItemMutation,
  useUpdateDropdownItemMutation,
  useDeleteDropdownItemMutation
} = templateAPI
